import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Box, ClickAwayListener, darken, FilledInput, lighten, Popper, PopperProps } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';

const AvailLanguageChip = ({
  children,
  onClick,
  enabled = false,
  values,
  options,
  isPointer,
  placement
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  enabled?: boolean;
  values?: string[];
  options?: string[];
  isPointer?: boolean;
  placement?: PopperProps['placement'];
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState('');
  const overflowOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const allOptions = useMemo(() => {
    if (!values) return [];
    return Array.from(new Set([...values, ...(options || [])])).sort();
  }, [values, options]);
  const filteredOptions = useMemo(
    () => allOptions.filter((option) => option.toLowerCase().includes(searchText.toLowerCase())),
    [allOptions, searchText]
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const handleEndAdornmentClick = (e: any) => {
    e.preventDefault();
    if (searchText.length > 0) {
      setSearchText('');
      setTimeout(() => {
        if (inputRef) {
          inputRef.current?.focus();
        }
      }, 100);
    } else {
      setSearchText('');
    }
  };

  const [curValues, setCurValues] = useState<string[]>(values || []);
  const curCount = useMemo(() => (values ? curValues.length : undefined), [curValues]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) return onClick(e);
    if (!values) return;
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleOverflowClick = (e: React.MouseEvent<HTMLDivElement>, val: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (curValues.includes(val)) {
      setCurValues(curValues.filter((v) => v !== val));
    } else {
      setCurValues([...curValues, val]);
    }
  };

  return (
    <StyledChip onClick={handleClick} enabled={+enabled} clickable={isPointer ? 1 : values?.length ? 1 : 0}>
      {curCount !== undefined && curCount}
      {children}
      {values && (
        <>
          <PolarisIcon icon="down" />
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Popper open={overflowOpen} anchorEl={anchorEl} placement={placement || 'bottom-end'}>
              <PopOverContainer onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                {allOptions.length > 15 && (
                  <StyledFilledInput
                    autoFocus
                    inputRef={inputRef}
                    sx={{ height: 37, minWidth: 420 }}
                    id="search-bar"
                    value={searchText}
                    inputProps={{
                      'data-testid': 'text-filter-input-focus-popper',
                      placeholder: 'Type to search'
                    }}
                    onChange={(event) => setSearchText(event.target.value)}
                    endAdornment={
                      <EndAdornmentIcon
                        type={searchText && searchText.length ? 'close' : 'search'}
                        icon={searchText && searchText.length ? 'close_filled' : 'search'}
                        onMouseDown={handleEndAdornmentClick}
                      />
                    }
                  />
                )}
                <OverflowContainer columns={5}>
                  {filteredOptions.map((option) => (
                    <AvailLanguageChip
                      isPointer
                      enabled={curValues?.includes(option)}
                      key={option}
                      onClick={(e) => (options ? handleOverflowClick(e, option) : undefined)}
                    >
                      {option}
                    </AvailLanguageChip>
                  ))}
                </OverflowContainer>
              </PopOverContainer>
            </Popper>
          </ClickAwayListener>
        </>
      )}
    </StyledChip>
  );
};

export default AvailLanguageChip;

const StyledChip = styled(Box)<{ enabled: number; clickable: number }>`
  min-width: 80px;
  background-color: ${({ theme, enabled }) =>
    enabled
      ? theme.palette.mode === 'dark'
        ? lighten(theme.palette.background.default, 0.2)
        : darken(theme.palette.background.default, 0.2)
      : 'transparent'};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1)}`};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: ${({ theme, enabled }) =>
    enabled
      ? 'none'
      : theme.palette.mode === 'dark'
        ? `1px dashed ${lighten(theme.palette.background.default, 0.2)}`
        : `1px dashed ${darken(theme.palette.background.default, 0.2)}`};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25em;
  transition: background 0.25s ease;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`;

const OverflowContainer = styled(Box)<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
  gap: 3px;
  max-height: 200px;
  overflow-y: auto;
`;

const PopOverContainer = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const StyledFilledInput = styled(FilledInput)`
  border: 0;
  width: 100%;
  transition: ease opacity 2000ms;
  margin-bottom: ${({ theme }) => theme.spacing(1)};

  input {
    padding: ${({ theme }) => theme.spacing(1.25, 2)};

    &:-webkit-autofill {
      transition: background-color 50000s ease-in-out 0s !important;
      -webkit-box-shadow: 0 0 0 0px transparent inset !important;
    }
  }

  &::placeholder {
    opacity: 0.7;
  }
  &:after,
  &:before {
    display: none;
    border: 0;
  }
`;

const EndAdornmentIcon = styled(PolarisIcon)<{ type: 'search' | 'close' }>`
  cursor: pointer;
  // color: ${({ theme, type }) => (type === 'search' ? theme.palette.primary.main : 'inherit')};
  transition: ease opacity 200ms;
  width: 25px;
  height: 25px;
  opacity: ${({ type }) => (type === 'close' ? 0.3 : 0.7)};

  &:hover {
    opacity: ${({ type }) => (type === 'close' ? 0.5 : 1)};
    transition: ease opacity 200ms;
  }
`;
